import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Skeleton } from 'antd';
import { Empty } from 'components/Empty';
import { Modal } from 'components/Modal';
import { Portal } from 'components/Shared/Portal';
import { Input } from 'components/UI/Input';
import { useDebounce } from 'hooks/useDebounce';
import * as Styled from './styles';
import { CategoryPicker } from '../CategoryPicker';
import { RedirectFooter } from '../RedirectFooter';
import { SearchListItem } from '../SearchListItem';
import { SearchListItemSkeleton } from '../SearchListItem/Skeleton';
import {
  getOmniSearchCollections,
  getOmniSearchCreator,
  getOmniSearchNfts
} from 'appRedux/restApi';
import { Close } from 'icons';
import { isMobile } from 'utils';
import { updateQuery } from 'utils/generic/query-route';
import { getPath } from 'utils/navigation/navigate';
import { Configs } from 'constants/app/configs';
import { ReactQueryKeys } from 'constants/app/react-query-keys';
import { INft } from 'interfaces';
import { TExploreProductType } from 'interfaces/explore';

type SearchModalProps = {
  isModalOpen: boolean;
  setIsModalOpen: (arg: boolean) => void;
};

export const SearchModal = ({
  isModalOpen,
  setIsModalOpen
}: SearchModalProps): JSX.Element => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchCategory, setSearchCategory] = useState<
    Extract<TExploreProductType, 'nfts' | 'collections'> | 'artists' | 'all'
  >('all');
  const [isInputActive, setIsInputActive] = useState(false);
  const searchLimitRef = useRef<number>();
  const history = useHistory();
  const location = useLocation();

  const {
    data: nfts,
    isRefetching: nftsIsLoading,
    refetch: nftsRefetch
  } = useQuery({
    queryKey: [ReactQueryKeys.SEARCH_NFT_LIST],
    queryFn: () =>
      getOmniSearchNfts({ name: searchValue, limit: searchLimitRef.current }),
    retry: false,
    enabled: false,
    staleTime: Infinity
  });

  const {
    data: collections,
    isRefetching: collectionsIsLoading,
    refetch: collectionsRefetch
  } = useQuery({
    queryKey: [ReactQueryKeys.SEARCH_COLLECTION_LIST],
    queryFn: () =>
      getOmniSearchCollections({
        name: searchValue,
        limit: searchLimitRef.current,
        is_empty_total_saleable_supply: 0
      }),
    retry: false,
    enabled: false,
    staleTime: Infinity
  });

  const {
    data: creator,
    isRefetching: creatorIsLoading,
    refetch: creatorRefetch
  } = useQuery({
    queryKey: [ReactQueryKeys.SEARCH_CREATOR_LIST],
    queryFn: () =>
      getOmniSearchCreator({
        user_name: searchValue,
        limit: searchLimitRef.current,
        is_empty_total_owned_saleable_supply: 0
      }),
    retry: false,
    enabled: false,
    staleTime: Infinity
  });

  const refetchData = () => {
    if (searchCategory === 'nfts') {
      searchLimitRef.current = 4;
      nftsRefetch();
    }
    if (searchCategory === 'collections') {
      searchLimitRef.current = 4;
      collectionsRefetch();
    }
    if (searchCategory === 'artists') {
      searchLimitRef.current = 4;
      creatorRefetch();
    }
    if (searchCategory === 'all') {
      searchLimitRef.current = 2;
      nftsRefetch();
      collectionsRefetch();
      creatorRefetch();
    }
  };

  const debounceTime = 1000;

  useDebounce(
    () => {
      refetchData();
    },
    debounceTime,
    [searchValue],
    true
  );

  useEffect(() => {
    refetchData();
  }, [searchCategory]);

  const handleCategoryChange = (
    value:
      | Extract<TExploreProductType, 'nfts' | 'collections'>
      | 'artists'
      | 'all'
  ) => {
    setSearchCategory(value);
  };

  const handleInputChange = (e: { target: { value: string } }) => {
    setIsInputActive(true);
    setSearchValue(e.target.value.toLowerCase());
  };

  const isSlugInUrl = (slug: string): boolean => {
    const path = location.pathname;
    const parts = path.split('/');
    return parts.some(el => el === slug);
  };

  const handleRedirect = (isForNft?: boolean, isCollections?: boolean) => {
    const searchQueryMap = {
      nfts: 'searchNft',
      collections: 'searchCollection',
      artists: 'searchArtist',
      all: ''
    };
    const newSearchCategory =
      searchCategory === 'all'
        ? isForNft
          ? 'nfts'
          : isCollections
          ? 'collections'
          : 'artists'
        : searchCategory;

    const searchFunction = searchQueryMap[newSearchCategory];

    const url = `${
      isSlugInUrl(Configs.PAGE_URLS.EXPLORE)
        ? ''
        : getPath(Configs.PAGE_URLS.EXPLORE)
    }/${newSearchCategory}${
      updateQuery({
        [searchFunction]: searchValue
      }).search
    }`;
    resetState();
    history.push(url);
    if (!isSlugInUrl(Configs.PAGE_URLS.EXPLORE)) {
      window.location.reload();
    }
  };

  const handleCancelClick = () => {
    resetState();
  };

  const resetState = () => {
    setIsModalOpen(false);
    setSearchValue('');
    setSearchCategory('all');
    setIsInputActive(false);
  };

  useEffect(() => {
    if (
      (searchCategory === 'all' &&
        !nftsIsLoading &&
        !collectionsIsLoading &&
        !creatorIsLoading &&
        nfts?.data.data.length === 0 &&
        collections?.data.data.length === 0 &&
        creator?.data.data.length === 0) ||
      (searchCategory === 'nfts' &&
        !nftsIsLoading &&
        nfts?.data.data.length === 0) ||
      (searchCategory === 'collections' &&
        !collectionsIsLoading &&
        collections?.data.data.length === 0) ||
      (searchCategory === 'artists' &&
        !creatorIsLoading &&
        creator?.data.data.length === 0)
    ) {
      setIsInputActive(false);
    } else {
      setIsInputActive(true);
    }
  }, [nfts, collections, creator]);

  const NftSearchResults = () => (
    <>
      <Styled.CategoryHeader>{t('generic.nfts')}</Styled.CategoryHeader>
      {nfts?.data.data.map((nft: INft) => (
        <SearchListItem key={nft.id} nft={nft} />
      ))}
      <RedirectFooter handleRedirect={() => handleRedirect(true, false)}>
        {t('generic.searchAllResults', { count: nfts?.data.meta.total })}
      </RedirectFooter>
    </>
  );

  const CollectionSearchResults = () => (
    <>
      <Styled.CategoryHeader>
        {t('collection.collections')}
      </Styled.CategoryHeader>
      {collections?.data.data.map(collection => (
        <SearchListItem key={collection.id} collection={collection}>
          <Styled.CollectionCounter>
            {collection.total_supply} {t('generic.nfts')}
          </Styled.CollectionCounter>
        </SearchListItem>
      ))}
      <RedirectFooter handleRedirect={() => handleRedirect(false, true)}>
        {t('generic.searchAllResults', {
          count: collections?.data.meta.total
        })}
      </RedirectFooter>
    </>
  );

  const CreatorSearchResults = () => (
    <>
      <Styled.CategoryHeader>{t('artist.creators')}</Styled.CategoryHeader>
      {creator?.data.data.map(creator => (
        <SearchListItem key={creator.id} creator={creator} />
      ))}
      <RedirectFooter handleRedirect={() => handleRedirect(false, false)}>
        {t('generic.searchAllResults', {
          count: creator?.data.meta.total
        })}
      </RedirectFooter>
    </>
  );

  const SearchBody = (
    <Styled.SearchWrapper>
      {isMobile() && (
        <Styled.SearchTitleWrapper>
          <Styled.SearchTitle>{t('generic.search')}</Styled.SearchTitle>
          <Styled.CloseButton onClick={handleCancelClick} size={'large'}>
            <Close />
          </Styled.CloseButton>
        </Styled.SearchTitleWrapper>
      )}

      <Styled.InputWrapper>
        <Input
          value={searchValue}
          onChange={handleInputChange}
          addonBefore={
            <CategoryPicker handleCategoryChange={handleCategoryChange} />
          }
          bordered={false}
          name="omni-search-input"
          placeholder={t('generic.tryTyping')}
          style={{ flexGrow: '1' }}
        />
        {!isMobile() && (
          <Styled.CancelButton onClick={handleCancelClick}>
            {t('generic.cancel')}
          </Styled.CancelButton>
        )}
      </Styled.InputWrapper>
      <Portal rootId="search-portal">
        {isInputActive ? (
          <Styled.SearchResults>
            {nftsIsLoading || collectionsIsLoading || creatorIsLoading ? (
              <>
                <Skeleton.Button
                  active
                  style={{ width: '100px', height: 18, margin: 0 }}
                  shape="round"
                />
                {Array.from({ length: 4 }, (_, index) => (
                  <SearchListItemSkeleton key={index} />
                ))}
                <Skeleton.Button
                  active
                  style={{ width: '150px', height: 18, margin: 0 }}
                  shape="round"
                />
              </>
            ) : (
              <>
                {!!nfts?.data.data.length && searchCategory === 'nfts' && (
                  <NftSearchResults />
                )}
                {!!collections?.data.data.length &&
                  searchCategory === 'collections' && (
                    <CollectionSearchResults />
                  )}
                {!!creator?.data.data.length &&
                  searchCategory === 'artists' && <CreatorSearchResults />}
                {(!!nfts?.data.data.length ||
                  !!collections?.data.data.length ||
                  !!creator?.data.data.length) &&
                  searchCategory === 'all' && (
                    <>
                      <NftSearchResults />
                      <CollectionSearchResults />
                      <CreatorSearchResults />
                    </>
                  )}
              </>
            )}
          </Styled.SearchResults>
        ) : searchValue ? (
          <Styled.EmptyWrapper>
            <Empty />
          </Styled.EmptyWrapper>
        ) : null}
      </Portal>
    </Styled.SearchWrapper>
  );

  return (
    <Modal
      isOpen={isModalOpen}
      closable={false}
      onClose={() => setIsModalOpen(false)}
      hideCloseIcon
      bodyTransparent
    >
      <Styled.Portal>
        {SearchBody}
        <div id="search-portal"></div>
      </Styled.Portal>
    </Modal>
  );
};
